import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

// Helpers
import showToast from '@/_lib/toast';

// Services and interfaces
import { IFood } from '@/interfaces/food/food';
import { useAddRecentFoodMutation } from '@/repositories/food_recent';
import { IRecipeIngredient } from '@/interfaces/recipe/recipe_builder';

// Styles
import { Add } from "@carbon/icons-react";

// Components
import Modal from "@/components/modal/modal";
import { ApiError } from '@/interfaces/api/error';
import FoodTable from '@/components/nutrition_builder/food_table';
import { mapDefaultUnit, mapUnit } from "@/_helpers/nutrition_helper";


interface _Props {
    open: boolean;
    onClose: () => void;
    selectedFoods?: string[];
    onAdd: (ingredients: IRecipeIngredient[]) => void;
}

const AddModal: FC<_Props> = ({
    open,
    onClose,
    selectedFoods,
    onAdd,
}) => {

    const { t } = useTranslation();
    const [selected, setSelected] = useState<any[]>([]);
    //REVIEW should we be doing this? these aren't foods really
    const [addFoods, { isLoading: isAddingFoods }] = useAddRecentFoodMutation();

    const mapFoods = (foods: IFood[]): IRecipeIngredient[] => {
        return foods.map((f) => {
            const units = f.units.map((u) => mapUnit(u));
            return {
                source_ingredient_id: f.id,
                name: f.name,
                amount: 100,
                unit: mapDefaultUnit(units),
                units: units,
                macros: {
                    calories: f.macros.calories,
                    protein: f.macros.protein,
                    carbs: f.macros.carbs,
                    fat: f.macros.fat,
                    fibre: f.macros.fibre,
                    alcohol: f.macros.alcohol ?? 0,
                },
            }
        });
    }

    // REVIEW - why is this being handled by internal state
    const handleSelectFood = (food: IFood) => setSelected([...selected, food]);
    const handleDeselectFood = (food: IFood) => setSelected(selected.filter((e) => e.id !== food.id));

    const handleAdd = () => {
        if (!selected || !onAdd) return;
        addFoods({foods: selected}).unwrap().then(() => {
            const foods = mapFoods(selected);
            onAdd(foods);
            onClose();
        }).catch((error: ApiError) => {
            showToast({type: 'error', apiError: error.type})
        })
    }

    return (
        <Modal 
            open={open}
            onClose={selected.length > 0 ? undefined : onClose}
            title={t('modals.nutritionFood')}
            children={
                <FoodTable 
                    selectedFoods={selectedFoods} 
                    onSelect={handleSelectFood} 
                    onDeselect={handleDeselectFood}
                />
            }
            action1={{
                kind: 'primary',
                label: t('components.buttons.add'),
                icon: <Add />,
                disabled: !selected.length,
                loading: isAddingFoods,
                onClick: handleAdd
            }}
            cancel={{
                label: t('components.buttons.cancel'),
                onClick: onClose
            }}
            sx={{'.MuiPaper-root': {width: '100%' ,maxWidth: '1400px'}}}
        />
    )
}

export default AddModal;